import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Linkedin, Link as LinkIcon } from 'lucide-react';
import { useState, useEffect } from 'react';
import SEO from '@/components/SEO';

// All chapter content - VERBATIM from the book
const CHAPTERS_CONTENT = {
  'prologue': {
    part: 'Introduction',
    number: 'PROLOGUE',
    title: 'The Meeting',
    readingTime: '8 min',
    author: 'Dr. Neha Dewan',
    pullQuotes: [
      'She counted them.',
      'By building a different one.'
    ],
    content: `She had prepared for three weeks.

Not because the meeting required three weeks of preparation. It did not. The analysis she brought to that room could have been assembled in an afternoon by someone who had been given the right to assemble it without apology. But she had learned, over the years, that preparation was her armour. That the more unimpeachable her data, the harder it became for the room to pretend she had not spoken.

So she prepared for three weeks.

She sat at the table. Not at the edge of it, where the junior people sat, where the people who were not quite sure they belonged sat, where the people who had been invited as a courtesy and would not be expected to contribute sat. She sat in the middle. Because she belonged in the middle. Because she had earned the middle. Because she had spent fifteen years earning the middle and she was not going to sit at the edge of the table she had spent fifteen years earning the right to sit at the centre of.

She spoke.

She laid out the analysis clearly. Structured. Evidence-based. The kind of presentation that, had it come from a different mouth, would have been described as authoritative. She watched faces as she spoke. She watched them the way you learn to watch faces in rooms that have not yet decided whether to hear you.

When she finished, the room was quiet.

Not the quiet of consideration. Not the quiet of people processing something important. The particular quiet of a room that has decided, collectively and without discussion, to respond to what was just said by saying nothing. The quiet that communicates, more efficiently than words, that the person who just spoke did not quite speak in the right register, at the right volume, with the right face, wearing the right history.

Four minutes passed.

{{PULLQUOTE:She counted them.}}

Four minutes later, a man on the opposite side of the table said almost exactly what she had said. He said it less precisely. He said it with less data. He said it in a way that was noticeably less prepared. But he said it with a particular quality of voice that certain rooms are designed to hear. The quality that says: I am not asking for permission to speak. I am simply speaking.

The room responded to him.

Not loudly. Not rudely. Worse than that: naturally. As if this was the first time the idea had been introduced to the conversation. As if the thing she had just spent three weeks preparing and six minutes presenting had not happened at all. The conversation moved forward on the back of his version of her idea. His name was attached to it in the follow-up email. His contribution was referenced in the minutes.

Her name was not in the minutes.

{{DIVIDER}}

You know this moment.

If you are a woman who has worked in any organisation, in any country, in any decade of the last fifty years, you know this moment. You have been in this room. You have sat at this table. You have watched your idea travel across the table and come back to you wearing someone else's name.

You know the particular discipline it requires to keep your face still while it happens. The years of practice it takes to transform the feeling that rises in your chest — hot and ancient and legitimate and useless in equal measure — into something that looks, from the outside, like professional composure.

You know what it costs.

Not just in that moment. Cumulatively. The compound interest of a thousand moments exactly like that one. The weight you carry from room to room, building to building, year to year. The calculation you make every morning before you walk into every meeting, every conversation, every space that was built before you arrived and has not yet decided whether to make room for you.

{{DIVIDER}}

And if you are a man reading this — you have watched this happen too.

You have been in that room. You have seen the idea travel. You may have been the man who repeated it, without realising what you were doing. Or you may have been the man who watched it happen and said nothing. Or — and this is the man this book is also written for — you may have been the man who noticed it, who named it, who refused to let the minutes erase what actually happened.

This book is for all of you.

Because the room this book describes is not a women's problem. It is a room problem. And every person who has ever sat in a room knows that rooms only change when the people in them decide to change them.

{{DIVIDER}}

I built this book because of that room.

Not the specific room where the specific woman sat on the specific afternoon I described above. I was not in that room. But I have been in rooms exactly like it. I have been that woman. I have counted those four minutes. I know what it feels like when your idea comes back to you wearing someone else's handwriting.

And I have spent the last year documenting five hundred and ninety-five women who were in that room — and who built anyway.

Not in spite of the room. Not by avoiding the room. Not by waiting for the room to change.

{{PULLQUOTE:By building a different one.}}`
  },

  'chapter-1': {
    part: 'Part One: Zero',
    number: 'CHAPTER ONE',
    title: 'What Fifty Years Found',
    readingTime: '14 min',
    author: 'Dr. Neha Dewan',
    pullQuotes: [
      'This is not a scandal. It is a diagnosis.',
      'The room changes when someone builds a different one.'
    ],
    content: `"We have spent billions on the wrong answer."
— The Research, 2026

In 1972, a researcher sat down with a stack of studies and a question she could not stop asking.

The question was simple. It should have had a simple answer.

The question was: what works?

What actually, measurably, reproducibly produces more women in positions of leadership over a sufficient period of time? Not which programs feel meaningful. Not which initiatives receive the most funding. Not which interventions produce the warmest testimonials from the women who went through them. Which ones actually move the needle on the only number that matters: the number of women who reach the top?

She expected to find answers.

The field was young then. Enthusiastic. Convinced that the problem was a knowledge problem — that organisations were simply unaware of their biases, uninformed about the barriers, unskilled in the practices that would allow women to advance. Give them the knowledge, ran the theory, and the system would correct itself. Train the women. Train the managers. Change the culture. Measure the outcomes.

She spent her career measuring the outcomes.

{{DIVIDER}}

Fifty years later, a different researcher sat down with a different stack of studies and the same question.

By 2026, the stack was not a stack anymore. It was a mountain. Thousands of papers. Hundreds of longitudinal studies. Decades of data from mentoring programs, leadership workshops, unconscious bias training, sponsorship initiatives, networking events, diversity targets, flexible working policies, women-only leadership academies, gender pay gap reporting requirements, board quotas, pipeline programs, and every other intervention that a well-resourced, well-intentioned, highly educated field of practice had been able to conceive of and implement over five decades of trying.

The question was the same: what works?

The answer is the number at the centre of this book.

{{ZERO}}

Zero.

Not a rounding error. Not a misreading of the data. Not a pessimistic interpretation of a complicated picture. Zero. The number of programs, interventions, initiatives or investments that have ever been measured over a sufficient period of time to demonstrate that they produce more women at the top.

Zero.

This is not a polemic. This is not a manifesto dressed as research. This is a finding. Peer-reviewed. Systematic. A nine-framework analysis of fifty years of evidence that arrives, with the same clinical precision that a medical trial arrives at its conclusion, at a number that should stop every organisation currently running a women's leadership program dead in its tracks.

{{DIVIDER}}

I want to be precise about what this finding means. Because precision matters here. The instinct, when confronted with Zero, is to reach immediately for the wrong interpretation.

Zero does not mean women cannot lead. Five hundred and ninety-five verified profiles say otherwise.

Zero does not mean organisations have not tried. They have tried extraordinarily hard, at extraordinary expense, with extraordinary sincerity.

Zero does not mean the women who went through these programs were not changed by them. Many were. Many carry those experiences as formative.

Zero means something more specific and more devastating than any of those things.

Zero means that not one of these programs has ever been measured over a long enough period, with a rigorous enough methodology, to demonstrate that it causes the organisation running it to have more women at the top than it would have had otherwise.

The organisations running these programs cannot prove they work. They are investing — in some cases, hundreds of millions of dollars annually — in solutions they cannot demonstrate solve the problem they were designed to solve.

{{PULLQUOTE:This is not a scandal. It is a diagnosis.}}

The programs are not solving the problem because the programs are aimed at women. The problem is not women. The problem is the system that was built before women arrived, and has not yet been persuaded to rebuild itself for them.

{{DIVIDER}}

I know what you are feeling right now.

If you have run one of these programs, you are feeling defensive. I understand that. You have seen women changed by your work. You have watched careers transform. You have witnessed confidence rebuilt in women who came to you depleted and left with something restored. That is real. I am not telling you it is not real.

I am telling you it is not enough.

If you have been through one of these programs, you might be feeling something more complicated. The program meant something to you. It may have changed you. And now a researcher is telling you it did not change the system. That the numbers above you are the same whether you went through the program or not. That all the investment — in time, in vulnerability, in the particular courage it takes to admit you need development in a room full of people who are all pretending they do not need it — did not move the needle.

That is not a comfortable thing to hear.

I am telling you anyway. Because the women in this book built their empires without waiting for a program to give them permission. And you deserve to know that the room you have been trying to change from inside has never been designed to change from inside.

{{PULLQUOTE:The room changes when someone builds a different one.}}`
  },

  'chapter-2': {
    part: 'Part One: Zero',
    number: 'CHAPTER TWO',
    title: 'The Labyrinth',
    readingTime: '11 min',
    author: 'Dr. Neha Dewan',
    pullQuotes: [
      'All of them reached the centre.'
    ],
    content: `"A maze has a wrong path. A labyrinth has only the path you walk."
— Ancient inscription

When the ancient Greeks built their first labyrinths, they were not building traps.

This is the misunderstanding most people carry. They hear labyrinth and they think maze: the puzzle with a single correct route and hundreds of wrong ones, the construction designed to disorient, the structure whose purpose is to defeat the person walking it.

A labyrinth is different.

A labyrinth has no wrong turns. Every path in a labyrinth leads to the centre. You cannot get lost in a labyrinth the way you get lost in a maze. You can only walk it. Slowly, or quickly. Directly, or circuitously. But if you keep walking, you will reach the centre. This is not a possibility. It is a certainty. It is the architectural truth of a labyrinth.

The difference between a maze and a labyrinth is the difference between a system designed to defeat you and a system designed to disorient you while you find your own way through.

The labyrinth of professional life for women is not a maze. There is no single correct path. There is no secret route that, once discovered, bypasses the confusion and deposits you cleanly at the top. There is only the walking. The particular, patient, relentless, sometimes furious, sometimes joyful, always costly walking.

The women in this book walked every possible path through the labyrinth.

Some walked quietly. Some walked loudly. Some walked around the obstacles. Some walked through them. Some built different paths when the ones they found were blocked. Some took paths that the labyrinth had not designed for women and walked them anyway, bearing the weight of being first, being watched, being the proof that it could be done.

{{PULLQUOTE:All of them reached the centre.}}

{{DIVIDER}}

I want to tell you about the nine paths.

The research behind this book identified nine frameworks through which the labyrinth operates. Nine different ways in which the professional world creates the particular obstacles that women carry and men, largely, do not. They do not operate sequentially. They do not respect industry boundaries or national borders or social class or the decade in which you were born. They operate simultaneously, overlapping, compounding, creating a texture of resistance that is experienced not as one big barrier — which would be easier to name and fight — but as ten thousand small ones.

{{FRAMEWORKS}}

These are the nine paths of the labyrinth.

The women in this book walked every one.`
  },

  'chapter-3': {
    part: 'Part One: Zero',
    number: 'CHAPTER THREE',
    title: 'What I Built',
    readingTime: '12 min',
    author: 'Dr. Neha Dewan',
    pullQuotes: [
      'Women\'s stories are lost by exactly the same mechanism.',
      'I wanted to write down the names.'
    ],
    content: `"I did not set out to build a platform. I set out to write down the names I kept forgetting."
— Dr. Neha Dewan

I am a researcher.

That is the sentence that surprises people when I tell them I built She Built This. Not because research is unglamorous — though it is, gloriously so — but because the connection between a woman who spends her days analysing governance frameworks and systems and the woman who spent her nights building a platform to document five hundred and ninety-five women who changed the world is not immediately obvious.

It is obvious to me.

The field I work in is, at its most fundamental level, the study of what happens when systems fail to monitor what matters. I have spent my career watching organisations lose not because they broke rules deliberately, not because they were led by bad people, but because the systems they relied upon were not watching closely enough to catch the gradual drift toward failure. The monitoring cycle was too infrequent. The threshold was crossed in month three. The review happened in month twelve. By then, five years of damage had already been done.

{{PULLQUOTE:Women's stories are lost by exactly the same mechanism.}}

Not one catastrophic erasure on one catastrophic day. Gradual drift. Incremental invisibility. The name that disappears from the minutes. The contribution that gets attributed elsewhere. The career that is described, after the fact, in terms of who supported her rather than what she built. The legacy that is present and then, slowly, imperceptibly, is not.

By the time anyone looks, the story is gone.

{{DIVIDER}}

She Built This began one afternoon in the spring of 2025.

I was reading an article about women in leadership. It was a good article. It named some women. It cited some statistics. And I found myself, at the end of it, frustrated in a way I could not immediately name.

It took me three days to name it.

The women in that article were real. Their achievements were real. But the article treated them as evidence for an argument about gender equity, rather than as people whose stories deserved to be told in full. They were instruments of a narrative about progress, rather than subjects of their own narratives about building. Their names were attached to their roles. Their roles were attached to statistics. And somewhere in that chain of instrumentalisation, the human being who had actually done the work of building disappeared.

I did not want to write another article.

{{PULLQUOTE:I wanted to write down the names.}}

Just the names, at first. And next to each name: what she built. Not what she represented. Not what her existence proved about the possibilities for women. What she built. Specifically, precisely, verifiably. The institution. The ministry. The hospital network. The peace negotiation. The technology company. The school that educated a hundred thousand children. The platform. The movement. The room.

I built She Built This in the evenings, after the governance work was done.

I verified every fact. I sourced every claim. I found the photographs. I wrote the profiles. I built the website. I registered the domain.

By the time I had one hundred profiles, I understood what I was doing.

By the time I had five hundred, I understood what it was for.

At five hundred and ninety-five, I stopped counting and started writing this book.`
  }
};

// Nine frameworks data
const NINE_FRAMEWORKS = [
  { num: '01', name: 'The Voice Framework', desc: 'What happens when you speak and the room does not hear you.' },
  { num: '02', name: 'The Sponsorship Framework', desc: 'What happens when you need someone to put your name forward in a room you are not in.' },
  { num: '03', name: 'The Visibility Framework', desc: 'What happens to the credit for work you have done.' },
  { num: '04', name: 'The Penalty Framework', desc: 'What it costs you to be ambitious in a system that calls ambition by a different name when it appears in women.' },
  { num: '05', name: 'The Networks Framework', desc: 'What happens when the relationships that matter are formed in spaces you are not invited into.' },
  { num: '06', name: 'The Pipeline Framework', desc: 'What happens when you are told there are not enough qualified women, in a world full of qualified women.' },
  { num: '07', name: 'The Governance Framework', desc: 'What happens when the rules were written before you arrived.' },
  { num: '08', name: 'The Crisis Framework', desc: 'What happens when everything is on fire and women are called to lead and then not credited for putting it out.' },
  { num: '09', name: 'The Legacy Framework', desc: 'What happens to women\'s stories after they are gone.' },
];

const CHAPTER_ORDER = ['prologue', 'chapter-1', 'chapter-2', 'chapter-3'];

const ChapterPage = () => {
  const { chapterSlug } = useParams();
  const [copied, setCopied] = useState(false);
  const [progress, setProgress] = useState(0);

  const chapter = CHAPTERS_CONTENT[chapterSlug];
  const currentIndex = CHAPTER_ORDER.indexOf(chapterSlug);
  const prevChapter = currentIndex > 0 ? CHAPTER_ORDER[currentIndex - 1] : null;
  const nextChapter = currentIndex < CHAPTER_ORDER.length - 1 ? CHAPTER_ORDER[currentIndex + 1] : null;

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      const scrollPercent = (scrollTop / docHeight) * 100;
      setProgress(Math.min(scrollPercent, 100));
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [chapterSlug]);

  const copyLink = () => {
    navigator.clipboard.writeText(window.location.href);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const shareLinkedIn = () => {
    const text = encodeURIComponent(`From "She Built This" by Dr. Neha Dewan — ${chapter.title}. Read at `);
    const url = encodeURIComponent(window.location.href);
    window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${url}`, '_blank');
  };

  // Handle locked chapters
  if (!chapter) {
    return (
      <div className="min-h-screen bg-[#FAF8F2] pt-24 flex items-center justify-center">
        <div className="text-center px-6">
          <h1 className="font-display text-4xl font-bold text-sbt-black mb-4">
            Chapter Locked
          </h1>
          <p className="font-body text-lg text-sbt-slate mb-8">
            This chapter is available in the complete book.
          </p>
          <Link to="/book#get-book" className="btn-primary">
            Get the Full Book
          </Link>
        </div>
      </div>
    );
  }

  // Render content with special formatting
  const renderContent = (content) => {
    const sections = content.split('\n\n');
    let isFirstParagraph = true;
    
    return sections.map((section, index) => {
      // Handle dividers
      if (section === '{{DIVIDER}}') {
        return (
          <div key={index} className="flex items-center justify-center gap-4 my-12">
            <div className="w-8 h-[1px] bg-sbt-gold" />
            <span className="text-sbt-gold">◆</span>
            <div className="w-8 h-[1px] bg-sbt-gold" />
          </div>
        );
      }

      // Handle Zero display
      if (section === '{{ZERO}}') {
        return (
          <div key={index} className="text-center my-16">
            <span className="font-display text-[120px] lg:text-[180px] font-bold text-sbt-green leading-none">
              0
            </span>
          </div>
        );
      }

      // Handle frameworks list
      if (section === '{{FRAMEWORKS}}') {
        return (
          <div key={index} className="my-12 space-y-4">
            {NINE_FRAMEWORKS.map((fw) => (
              <div key={fw.num} className="bg-[#FAF8F2] border-l-4 border-sbt-green p-6">
                <div className="flex items-start gap-4">
                  <span className="font-mono text-2xl font-bold text-sbt-green">{fw.num}</span>
                  <div>
                    <h4 className="font-display text-xl font-bold text-sbt-black mb-1">{fw.name}</h4>
                    <p className="font-body text-sbt-dim">{fw.desc}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        );
      }

      // Handle pull quotes
      if (section.startsWith('{{PULLQUOTE:')) {
        const quote = section.replace('{{PULLQUOTE:', '').replace('}}', '');
        return (
          <blockquote key={index} className="my-10 p-6 bg-[#EEF8F2] border-l-4 border-sbt-green">
            <p className="font-display text-2xl lg:text-[26px] italic text-sbt-slate leading-relaxed">
              {quote}
            </p>
          </blockquote>
        );
      }

      // Handle quotes (lines starting with ")
      if (section.startsWith('"') && section.includes('—')) {
        return (
          <p key={index} className="font-body text-lg italic text-sbt-dim text-center my-8">
            {section}
          </p>
        );
      }

      // Regular paragraph with drop cap for first
      const isDropCap = isFirstParagraph && !section.startsWith('"');
      if (isDropCap) isFirstParagraph = false;

      return (
        <p key={index} className="font-body text-[18px] text-[#2A2A2A] leading-[1.9] mb-6">
          {isDropCap ? (
            <>
              <span className="float-left font-display text-[72px] leading-[0.8] mr-3 mt-2 text-sbt-green font-bold">
                {section.charAt(0)}
              </span>
              {section.slice(1)}
            </>
          ) : (
            section
          )}
        </p>
      );
    });
  };

  return (
    <div data-testid="chapter-page" className="min-h-screen bg-[#FAF8F2]">
      <SEO 
        title={`${chapter.number}: ${chapter.title} — She Built This Book`}
        description={`Read ${chapter.title} from She Built This by Dr. Neha Dewan. ${chapter.readingTime} read.`}
        keywords={['She Built This book', 'Dr. Neha Dewan', chapter.title, 'women leadership']}
        url={`https://shebuiltthis.com/book/${chapterSlug}`}
        image="https://customer-assets.emergentagent.com/job_22d79c15-8eba-437a-975f-6355374d527d/artifacts/rgvc8ddx_SBT_COVER_FRONT.png"
        type="article"
        author="Dr. Neha Dewan"
      />
      {/* Reading progress bar */}
      <div className="fixed top-0 left-0 right-0 h-1 bg-sbt-border z-50">
        <div 
          className="h-full bg-sbt-green transition-all duration-150"
          style={{ width: `${progress}%` }}
        />
      </div>

      {/* Sticky navigation */}
      <nav className="sticky top-0 bg-[#FAF8F2] border-b border-sbt-border z-40 py-4">
        <div className="max-w-[1000px] mx-auto px-6 flex items-center justify-between">
          <Link
            to="/book"
            className="font-mono text-sm text-sbt-dim hover:text-sbt-gold transition-colors flex items-center gap-2"
          >
            <ArrowLeft size={16} />
            She Built This
          </Link>
          <span className="font-mono text-[13px] text-sbt-slate hidden sm:block">
            {chapter.number}: {chapter.title}
          </span>
          <span className="font-mono text-[11px] text-sbt-dim">
            {Math.round(progress)}% read
          </span>
        </div>
      </nav>

      {/* Gold rule */}
      <div className="h-[1px] bg-sbt-gold" />

      {/* Chapter content */}
      <article className="max-w-[680px] mx-auto px-6 py-16">
        {/* Chapter header */}
        <header className="text-center mb-16">
          <p className="font-mono text-[12px] text-sbt-green tracking-wider uppercase mb-2">
            {chapter.part}
          </p>
          <p className="font-mono text-[13px] text-sbt-dim tracking-wider mb-6">
            {chapter.number}
          </p>
          <h1 className="font-display text-4xl lg:text-[56px] font-bold text-sbt-black leading-tight mb-8">
            {chapter.title}
          </h1>
          
          {/* Gold ornament */}
          <div className="flex items-center justify-center gap-4 mb-8">
            <div className="w-[80px] h-[1px] bg-sbt-gold" />
            <div className="w-2 h-2 bg-sbt-gold rotate-45" />
            <div className="w-[80px] h-[1px] bg-sbt-gold" />
          </div>
          
          <p className="font-mono text-[11px] text-sbt-dim tracking-wide mb-4">
            {chapter.readingTime} read
          </p>
          <p className="font-subheading text-[15px] italic text-sbt-dim">
            {chapter.author}
          </p>

          {/* Share buttons */}
          <div className="flex items-center justify-center gap-4 mt-6">
            <button
              onClick={shareLinkedIn}
              className="flex items-center gap-2 px-4 py-2 border border-sbt-border text-sbt-dim hover:text-sbt-gold hover:border-sbt-gold transition-colors text-sm"
            >
              <Linkedin size={14} /> Share on LinkedIn
            </button>
            <button
              onClick={copyLink}
              className="flex items-center gap-2 px-4 py-2 border border-sbt-border text-sbt-dim hover:text-sbt-gold hover:border-sbt-gold transition-colors text-sm"
            >
              <LinkIcon size={14} /> {copied ? 'Copied!' : 'Copy Link'}
            </button>
          </div>
        </header>

        {/* Content */}
        <div className="prose prose-lg max-w-none">
          {renderContent(chapter.content)}
        </div>

        {/* Chapter footer */}
        <footer className="mt-16 pt-8 border-t border-sbt-gold">
          {/* Navigation */}
          <div className="flex items-center justify-between mb-12">
            {prevChapter ? (
              <Link
                to={`/book/${prevChapter}`}
                className="flex items-center gap-2 font-mono text-sm text-sbt-dim hover:text-sbt-gold transition-colors"
              >
                <ArrowLeft size={16} />
                Previous Chapter
              </Link>
            ) : (
              <div />
            )}
            {nextChapter ? (
              <Link
                to={`/book/${nextChapter}`}
                className="flex items-center gap-2 font-mono text-sm text-sbt-dim hover:text-sbt-gold transition-colors"
              >
                Next Chapter
                <ArrowRight size={16} />
              </Link>
            ) : (
              <Link
                to="/book#get-book"
                className="flex items-center gap-2 font-mono text-sm text-sbt-green hover:underline"
              >
                Get the Full Book
                <ArrowRight size={16} />
              </Link>
            )}
          </div>

          {/* End of chapter actions */}
          <div className="bg-sbt-black p-8 text-center">
            <p className="font-mono text-xs text-sbt-gold tracking-wider uppercase mb-4">
              You've just read {chapter.number}
            </p>
            <h3 className="font-display text-2xl font-bold text-white mb-6">
              {chapter.title}
            </h3>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              {nextChapter ? (
                <Link to={`/book/${nextChapter}`} className="btn-primary">
                  Read the next chapter →
                </Link>
              ) : (
                <Link to="/book#get-book" className="btn-primary">
                  Get the full book →
                </Link>
              )}
              <button
                onClick={shareLinkedIn}
                className="btn-secondary"
              >
                Share this chapter
              </button>
            </div>
            <Link to="/nominate" className="block mt-6 font-mono text-xs text-sbt-dim hover:text-sbt-gold transition-colors">
              Know a woman who belongs on She Built This? Nominate her →
            </Link>
          </div>
        </footer>
      </article>
    </div>
  );
};

export default ChapterPage;
